import React, { Dispatch, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mdiViewHeadline, mdiViewSequential, mdiViewStream } from '@mdi/js';
import Icon from '@mdi/react';
import { List, ListItemButton, ListItemIcon, ListItemText, Radio } from '@mui/material';
import { GridDensity } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { updateDensity } from '../../../redux/user/sliceUserSettings';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, vars } from '../../../Theme';
import { getGridTranslations } from './DocTableUtils';

/**
 * Component that allows the user to change the row-density of the Document Table.
 */
export default () => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const gridDensity = useSelector((store: IAppReduxStore) => store.userSettings.data.gridDensity);

    /** Gets the DataGrid's translation data for the current application language. */
    const translations = useMemo(() => getGridTranslations(), [i18n.language]);

    /**
     * Gets the icon for a given DataGrid row density.
     * @param density - Target DataGrid row density.
     */
    const getListItemIcon = useCallback((density: GridDensity) => {
        if (density === 'compact') {
            return mdiViewHeadline;
        }

        return density === 'standard' ? mdiViewSequential : mdiViewStream;
    }, []);

    /**
     * Gets the display text for a given DataGrid row density.
     * @param density - Target DataGrid row density.
     */
    const getListItemText = useCallback(
        (density: GridDensity) => {
            if (density === 'compact') {
                return translations.toolbarDensityCompact;
            }

            return density === 'standard'
                ? translations.toolbarDensityStandard
                : translations.toolbarDensityComfortable;
        },
        [translations],
    );

    /**
     * Creates a list item for a given DataGrid row density.
     * @param density - Target DataGrid row density.
     */
    const listItem = useCallback(
        (density: GridDensity) => (
            <ListItemButton
                onClick={() => dispatch(updateDensity({ gridDensity: density }))}
                sx={{
                    color: density === gridDensity ? vars.brandGreen : vars.font,
                    paddingLeft: 0,
                }}
            >
                <Radio
                    checked={density === gridDensity}
                    value={density}
                    name='density-radio-button'
                    inputProps={{ 'aria-label': 'density' }}
                />
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                    <Icon
                        path={getListItemIcon(density)}
                        size={1}
                        color={density === gridDensity ? vars.brandGreen : vars.font}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={getListItemText(density)}
                    sx={{ ...headingFontBold, fontSize: 14, ml: 1 }}
                />
            </ListItemButton>
        ),
        [gridDensity],
    );

    return (
        <List>
            {listItem('compact')}
            {listItem('standard')}
            {listItem('comfortable')}
        </List>
    );
};
